import { useCallback, useEffect, useRef } from "react";

/**
 * Gestaltung zur Laufzeit (design/README §3.1, §4.4, §9.8; Referenz `design/werkzeuge/vorschau/logik.js`): was das
 * Referenz-CSS nicht allein kann, misst die App wie die Vorschau.
 *
 * - **Wortbreite** `--wort` je Begriff (Canvas `measureText`): ein Begriff wächst mit der Systemschrift, aber nie über die
 *   Breite seines Elements; gleichartige Kacheln einer Gruppe (`data-gleich`) teilen das breiteste Wort (Review 0d O-06).
 * - **Zeilen des Begriffs** `--k-zeilen`: bricht ein Begriff um, gibt das Symbol Höhe ab.
 * - **Scrollbereiche** (`.scroll[data-liste]`) füllen ihren Platz und scrollen nativ ohne Einrasten (README §14 N11, statt
 *   O-01); der Hinweisstreifen `.mehr` daneben ist ein `button` „<n> weitere“ bzw. „<n> ältere“ (sichtbar mit Dreieck, O-04,
 *   oberflaeche.md §1.6 V-25) und blättert beim Antippen um eine sichtbare Höhe weiter, ein Verlauf (`data-liste="auf"`) ist
 *   unten verankert.
 * - **Ausrichtung** `data-ausrichtung="quer"` (bei Höhe ≤ 360 px zusätzlich `data-hoehe="flach"`) an der Ansicht (Querformat-Raster, Review 0e F-01); ein Container fragt
 *   sich nicht selbst ab.
 *
 * Die Ansicht ruft das über `useGestaltung(ref)` auf; neu gemessen wird bei jeder Größenänderung, jeder Änderung des
 * Inhalts (Text, Elemente), nach dem Laden der Schrift und beim Blättern (nur der Hinweisstreifen).
 */

const leinwand = typeof document !== "undefined" ? document.createElement("canvas").getContext("2d") : null;

/** Breite des längsten Wortes in em (Wörter an Leerzeichen und nach geschriebenem Bindestrich); `ganz`: der ganze Text. */
function wortbreite(el: Element, ganz: boolean): number {
  if (!leinwand) return 1;
  const cs = getComputedStyle(el);
  leinwand.font = `${cs.fontWeight} 100px ${cs.fontFamily}`;
  const gross = cs.textTransform === "uppercase";
  const laufweite = Math.max(0, parseFloat(cs.letterSpacing) / parseFloat(cs.fontSize) || 0);
  const text = (el.textContent ?? "").trim();
  if (!text) return 0;
  const woerter = (ganz ? [text] : text.split(/\s+|(?<=-)/)).map((w) => (gross ? w.toUpperCase() : w));
  return Math.max(...woerter.map((w) => leinwand.measureText(w).width / 100 + laufweite * w.length)) + 0.12;
}

const BEGRIFFE = ".k__b, .zs__begriff, .zs__zeit, .titel__t, .zaehler__b, .best__b, .kopf, .dialog__kopf, .neutral__t";
const GANZ = ".zs__begriff, .zs__zeit, .zaehler__b";
const ZEILEN = ".scroll :is(.zeile__t, .zeile__u, .eintrag__t, .eintrag__meta, .eintrag > span), .feld-text__b, .liste__schutz > .liste__text, "
  + ".liste__lage > :is(b, span), .liste__abdruck > .liste__text, .start__lage > .liste__text, .liste__zahlen .zahl > span, .info > p, "
  + ".kontaktbitte :is(.kontaktbitte__t > span, .hinweistext__t), .klappliste__option";

function woerter(wurzel: HTMLElement): void {
  for (const b of wurzel.querySelectorAll<HTMLElement>(BEGRIFFE)) {
    const w = wortbreite(b, b.matches(GANZ)).toFixed(2);
    (b.matches(".k__b") ? b.parentElement! : b).style.setProperty("--wort", w);
  }
  for (const b of wurzel.querySelectorAll<HTMLElement>(ZEILEN)) b.style.setProperty("--wort", wortbreite(b, false).toFixed(2));
  for (const reihe of wurzel.querySelectorAll<HTMLElement>("[data-gleich]")) {
    const kacheln = [...reihe.querySelectorAll<HTMLElement>(":scope > .k, :scope > * > .k")];
    if (!kacheln.length) continue;
    const m = Math.max(...kacheln.map((k) => parseFloat(k.style.getPropertyValue("--wort")) || 0));
    for (const k of kacheln) k.style.setProperty("--wort", m.toFixed(2));
  }
  for (const b of wurzel.querySelectorAll<HTMLElement>(".k > .k__b")) {
    const k = b.parentElement!;
    const zeile = parseFloat(getComputedStyle(b).fontSize) * 1.2 || 1;
    const text = [...k.children].filter((c) => !c.matches("svg, .k__marke, .k__chip, .k__beschreibung") && c.getClientRects().length)
      .reduce((s, c) => s + c.getBoundingClientRect().height, 0);
    const z = Math.max(1, Math.round((text / zeile) * 10) / 10);
    if (z > 1.05) k.style.setProperty("--k-zeilen", z.toFixed(2)); else k.style.removeProperty("--k-zeilen");
  }
}

/**
 * Einträge eines Scrollbereichs: Bereiche (auch auf Platte), Tabellen und Gruppen sind keine Einträge, ihre Kinder schon.
 * Ebenso ein gegliederter Eintrag (`eintrag--gegliedert`, z. B. ein Mitwirkender der Einteilung mit einem Ankreuzfeld je
 * Rolle), der höher ist als jeder Bildschirm: Seine Zeilen sind die Einträge, sonst endete die Liste vor ihm an seiner
 * Überschrift und darunter bliebe die Fläche leer (Bericht N6 F10).
 */
function eintraege(s: Element): Element[] {
  const tief = (el: Element): Element[] => (el.matches(".gruppe, .liste-bereich, .text-bereich, .anrufzeilen, .anrufzeilen > tbody, .tabelle, .tabelle > tbody, .zaehler-reihe, .filter, .knopfreihe, .eintrag--gegliedert")
    ? [...el.children].flatMap(tief) : [el]);
  return [...s.children].flatMap(tief).filter((c) => c.getClientRects().length > 0);
}

/**
 * Inhalt des Hinweisstreifens (oberflaeche.md §1.6, V-25): zugänglicher Name „<n> weitere“ bzw. „<n> ältere“, davor das Dreieck
 * nur für die Augen (`aria-hidden`). Ohne Einträge außerhalb des sichtbaren Bereichs gibt es keinen Streifen (`null`).
 */
export function hinweisInhalt(n: number, auf: boolean): { zeichen: string; name: string } | null {
  if (n <= 0) return null;
  return auf ? { zeichen: "▴", name: `${n} ältere` } : { zeichen: "▾", name: `${n} weitere` };
}

/** Weite, um die das Antippen des Hinweisstreifens blättert: eine sichtbare Höhe der Liste (V-25), nach oben im Verlauf. */
export function blaetterweite(sichtbareHoehe: number, auf: boolean): number {
  return (auf ? -1 : 1) * Math.max(sichtbareHoehe, 1);
}

function hinweis(s: HTMLElement): void {
  const streifen = s.parentElement?.querySelector<HTMLElement>(":scope > .mehr");
  if (!streifen) return;
  const auf = s.dataset["liste"] === "auf";
  const sr = s.getBoundingClientRect();
  // ein gegliederter Eintrag zählt einmal, auch wenn mehrere seiner Zeilen außerhalb liegen
  const n = new Set(eintraege(s).filter((c) => {
    const r = c.getBoundingClientRect();
    return auf ? r.top < sr.top - 1 : r.bottom > sr.bottom + 1;
  }).map((c) => c.closest(".eintrag--gegliedert") ?? c)).size;
  const inhalt = hinweisInhalt(n, auf);
  streifen.classList.toggle("ist-an", inhalt !== null);
  // verborgen (ohne Einträge außerhalb) ist der Streifen kein Bedienelement: nicht fokussierbar, nicht im Bedienungshilfenbaum
  streifen.tabIndex = inhalt ? 0 : -1;
  const name = inhalt?.name ?? "";
  if (streifen.dataset["name"] === name) return;
  streifen.dataset["name"] = name;
  if (!inhalt) { streifen.replaceChildren(); return; }
  const zeichen = document.createElement("span");
  zeichen.className = "mehr__zeichen";
  zeichen.setAttribute("aria-hidden", "true");
  zeichen.textContent = inhalt.zeichen;
  const text = document.createElement("span");
  text.textContent = inhalt.name;
  streifen.replaceChildren(zeichen, " ", text);
}

const beobachtet = new WeakSet<HTMLElement>();

/**
 * Stelle jedes Scrollbereichs nach dem letzten Blättern bzw. der letzten Gestaltung. Kommen Einträge hinzu, hält der
 * Browser (Scroll-Verankerung) den zuvor sichtbaren Eintrag – auch wenn davor Einträge hinzukamen und die Liste dadurch
 * nicht mehr am Anfang steht. Stand sie am Anfang (Verlauf: am Ende), bleibt sie dort (`ausstehend` bis zur nächsten
 * Gestaltung); wer mitten in der Liste steht, behält seinen Eintrag.
 */
const stand = new WeakMap<HTMLElement, { oben: number; ende: boolean }>();
const ausstehend = new WeakSet<HTMLElement>();
const merken = (s: HTMLElement) => stand.set(s, { oben: s.scrollTop, ende: s.scrollHeight - s.scrollTop - s.clientHeight < 2 });

function inhaltGeaendert(eintraege: MutationRecord[]): void {
  for (const e of eintraege) {
    if (e.type !== "childList") continue;
    const ziel = e.target instanceof Element ? e.target : e.target.parentElement;
    const s = ziel?.closest<HTMLElement>(".scroll[data-liste]");
    if (s && !ziel?.closest(".mehr")) ausstehend.add(s);
  }
}

/** Blättern merken; die Verankerung nach einer Inhaltsänderung verschiebt eine Liste am Anfang (Verlauf: am Ende) nicht. */
function geblaettert(s: HTMLElement): void {
  const alt = stand.get(s);
  const amRand = alt && (s.dataset["liste"] === "auf" ? alt.ende : alt.oben < 1);
  if (!(ausstehend.has(s) && amRand)) merken(s);
}

/** Höhe des Inhalts eines Scrollbereichs, unabhängig von seiner gesetzten Höhe (bis zur Unterkante des letzten Kindes). */
function inhaltshoehe(s: HTMLElement): number {
  const kinder = [...s.children].filter((c) => c.getClientRects().length > 0);
  const letztes = kinder.at(-1);
  if (!letztes) return 0;
  const unten = Math.max(...kinder.map((c) => c.getBoundingClientRect().bottom));
  return unten - s.getBoundingClientRect().top + s.scrollTop + (parseFloat(getComputedStyle(letztes).marginBottom) || 0)
    + (parseFloat(getComputedStyle(s).paddingBottom) || 0);
}

/**
 * Höhe der Scrollbereiche: Der Bereich füllt seinen Platz (README §14 N11); ohne Einrasten hielte eine Grenze am letzten
 * ganzen Eintrag beim Blättern nicht, und vor einem hohen Eintrag (Überschrift und erste Zeile) bliebe unter einer kurzen
 * Liste ein Loch. Gemessen wird, ohne die Höhe zurückzusetzen, und `scrollTop` wird nur gesetzt, wo eine Stelle gehalten
 * werden muss (Verlauf am Ende, Liste am Anfang nach neuem Inhalt): Jedes Schreiben während einer Wischgeste bricht auf
 * iOS den Schwung ab (Meldung N11: „bleibt hängen, geht maximal einen Punkt nach unten“).
 */
function scrollGrenzen(wurzel: HTMLElement): void {
  for (const s of wurzel.querySelectorAll<HTMLElement>(".scroll[data-liste]")) {
    const rahmen = s.parentElement;
    if (!rahmen) continue;
    const erstes = !beobachtet.has(s);
    const auf = s.dataset["liste"] === "auf";
    if (erstes) {
      s.addEventListener("scroll", () => { hinweis(s); geblaettert(s); }, { passive: true });
      // „▾ n weitere“ verdeckt nichts (eigene Zeile) und blättert beim Antippen um eine sichtbare Höhe weiter (N11, V-25)
      rahmen.querySelector<HTMLElement>(":scope > .mehr")?.addEventListener("click", () => {
        s.scrollBy({ top: blaetterweite(s.clientHeight, auf), behavior: "smooth" });
      });
      beobachtet.add(s);
    }
    const gemerkt = stand.get(s);
    const geschuetzt = ausstehend.has(s) && gemerkt && (auf ? gemerkt.ende : gemerkt.oben < 1);
    ausstehend.delete(s);
    // ein Verlauf bleibt unten, wenn er unten stand (neue Nachricht); wer gerade blättert, behält seine Stelle
    const amEnde = erstes || geschuetzt || s.scrollHeight - s.scrollTop - s.clientHeight < 2;
    s.style.maxHeight = "";
    const kopf = rahmen.querySelector<HTMLElement>(":scope > .liste__zus");
    const platz = rahmen.getBoundingClientRect();
    const streifen = rahmen.querySelector<HTMLElement>(":scope > .mehr");
    const sh = streifen ? Math.max(streifen.getBoundingClientRect().height, auf ? 0 : 24) : 0;
    const kh = kopf && kopf.getClientRects().length ? kopf.getBoundingClientRect().height + parseFloat(getComputedStyle(kopf).marginBottom) : 0;
    const verfuegbar = Math.max(0, platz.height - sh - kh);
    const hoehe = inhaltshoehe(s) <= verfuegbar + 1 ? "auto" : `${verfuegbar.toFixed(2)}px`;
    if (s.style.height !== hoehe) s.style.height = hoehe;
    if (auf && amEnde) {
      if (s.scrollHeight - s.scrollTop - s.clientHeight >= 1) s.scrollTop = s.scrollHeight;
    } else if (geschuetzt && Math.abs(s.scrollTop - gemerkt.oben) >= 1) {
      s.scrollTop = gemerkt.oben;
    }
    merken(s);
    hinweis(s);
  }
}

/** Mindesthöhe des sichtbaren Listenbereichs je höchstem Eintrag (Anforderungen K101: ein ganzer Eintrag und der Anfang des nächsten). */
export const LISTENBEREICH_JE_EINTRAG = 1.25;

/**
 * Höhe des höchsten Eintrags einer Liste: Einträge mit eigenem Inhalt (`article`) bzw. Einträge, die eine Ansicht öffnen
 * (`a[href]`), nicht ineinander (oberflaeche.md §1.2, §1.5; wie die Messung der Suite, TESTSPRACHE §2).
 */
export function hoechsterEintrag(s: Element): number {
  const alle = [...s.querySelectorAll("article, [role=article], a[href]")].filter((e) => e.getClientRects().length > 0);
  const eintraege = alle.filter((e) => !alle.some((a) => a !== e && a.contains(e)));
  return Math.max(0, ...eintraege.map((e) => e.getBoundingClientRect().height));
}

/**
 * Stufen der niedrigen Höhe (Anforderungen K101 (3)–(5), oberflaeche.md §1.6 Nr. 5 V-26) in ihrer Reihenfolge; (1) Querformat
 * zweispaltig und (2) Kopfnavigation einreihig gelten immer (app.css). Jede Stufe behält die vorigen.
 * - `band`: Zustandsband kompakt in einer Zeile („Zurück“, Statusleiste, „mehr“; der Titel nur für Hilfsmittel, Wechsel- und
 *   Kontaktband bleiben); in neutralen Ansichten ebenso der Titel über der Liste (`main` trägt den Ansichtsnamen, das Ziel der
 *   Kopfnavigation ist markiert);
 * - `handlungen`: Handlungen der Ansicht von hinten hinter „mehr“ (`Handlungen.tsx`), die erste bleibt direkt sichtbar;
 * - `eintraege`: Einträge kompakt (Angaben fließend, Handlungen in einer Reihe).
 */
export const STUFEN = ["band", "handlungen", "eintraege"] as const;
export type Stufe = (typeof STUFEN)[number];

/** Verletzt die Liste die Regel (nur, wenn sie überhaupt scrollt: passt sie ganz, ist nichts zu gewinnen)? */
export function regelVerletzt(liste: { sichtbar: number; inhalt: number }, eintrag: number): boolean {
  return eintrag > 0 && liste.inhalt > liste.sichtbar + 1 && liste.sichtbar < LISTENBEREICH_JE_EINTRAG * eintrag;
}

/** Nächste Stufe, die in dieser Ansicht etwas bewirkt (`wirkt`), nach der aktuellen; `null`, wenn es keine mehr gibt. */
export function naechsteStufe(aktuell: Stufe | null, wirkt: (s: Stufe) => boolean): Stufe | null {
  const ab = aktuell ? STUFEN.indexOf(aktuell) + 1 : 0;
  return STUFEN.slice(ab).find(wirkt) ?? null;
}

const groesseDerStufe = new WeakMap<HTMLElement, string>();

/**
 * Niedrige Höhe: misst nach dem Gestalten den sichtbaren Bereich der Liste von `main` und ihren höchsten Eintrag und setzt,
 * solange die Regel verletzt ist, die nächste wirksame Stufe als `data-k101` an der Ansicht (eine je Bild; danach wird neu
 * gemessen). Im Querformat zählt zusätzlich, dass die Handlungen in der linken Spalte unter das Zustandsband passen. Ändern
 * sich Größe oder Schrift der Ansicht, beginnt es wieder ohne Stufe. Gesetzt wird nur das Attribut, nie eine Scrollposition.
 * Liefert, ob sich die Stufe geändert hat.
 */
export function niedrigeHoehe(wurzel: HTMLElement): boolean {
  const groesse = `${wurzel.clientWidth}x${wurzel.clientHeight}@${getComputedStyle(document.documentElement).fontSize}`;
  const vorher = (wurzel.dataset["k101"] as Stufe | undefined) ?? null;
  if (groesseDerStufe.get(wurzel) !== groesse) {
    groesseDerStufe.set(wurzel, groesse);
    if (vorher) { delete wurzel.dataset["k101"]; return true; }
  }
  const main = wurzel.querySelector<HTMLElement>(":scope > main");
  const s = main?.querySelector<HTMLElement>(".scroll[data-liste]");
  if (!main || !s) return false;
  // Kopf einer Ansicht: Zustandsband bzw. in neutralen Ansichten der Titel über der Liste
  const band = wurzel.querySelector<HTMLElement>(":scope > .band--liste");
  const kopf = band ?? main.querySelector<HTMLElement>(":scope > .neutral__kopf");
  const reihe = main.querySelector<HTMLElement>(":scope > .handlungen");
  const quer = wurzel.dataset["ausrichtung"] === "quer";
  const verdeckt = quer && !!band && !!reihe && reihe.getClientRects().length > 0
    && reihe.getBoundingClientRect().top < band.getBoundingClientRect().bottom - 0.5;
  if (!verdeckt && !regelVerletzt({ sichtbar: s.clientHeight, inhalt: s.scrollHeight }, hoechsterEintrag(s))) return false;
  const wirkt = (st: Stufe) => st === "band" ? !!kopf && !quer
    : st === "handlungen" ? !!reihe?.querySelector(":scope > .handlungen__mehr") : !verdeckt;
  const neu = naechsteStufe(vorher, wirkt);
  if (!neu) return false;
  wurzel.dataset["k101"] = neu;
  return true;
}

function ausrichtung(wurzel: HTMLElement): void {
  const quer = wurzel.offsetWidth > wurzel.offsetHeight;
  if (quer) wurzel.dataset["ausrichtung"] = "quer"; else delete wurzel.dataset["ausrichtung"];
  // sehr flaches Querformat (Höhe ≤ 360 px, 568 × 320; README §14 D1 0f-3, Referenz logik.js `data-hoehe`)
  if (quer && wurzel.offsetHeight <= 360) wurzel.dataset["hoehe"] = "flach"; else delete wurzel.dataset["hoehe"];
}

/** Misst die Ansicht neu (Ausrichtung, Wortbreiten, Scrollbereiche). */
/** Fest unten stehende Handlung eines Dialogs (app.css, oberflaeche.md §1.6 Nr. 4, V-25). */
const DIALOG_HANDLUNG = ":scope > .knopfreihe:last-child, :scope > :last-child > .knopfreihe:last-child";

/**
 * Ein Feld, das den Fokus erhält (Bildschirmtastatur), oder ein Element, das sich in den Blick holt, rückt der Browser nur bis
 * an den unteren Rand des Scrollbereichs – genau dorthin, wo im Dialog die fest stehende Handlung liegt; das Feld läge dann
 * unter „Speichern“ (Meldung iPhone SE, „Telefonnummer“ im Dialog „Teilnehmer“). `scroll-padding-bottom` in Höhe der Handlung
 * hält solche Felder darüber. Gesetzt wird nur der Abstand, nie eine Scrollposition.
 */
const dialogeMitFokushilfe = new WeakSet<HTMLElement>();

/**
 * Nicht jeder Browser beachtet `scroll-padding` beim Fokus (WebKit unter Linux nicht, bei Safari nicht zugesichert): Liegt ein
 * Feld nach dem Fokus noch unter der fest stehenden Handlung, rückt der Dialog es um das fehlende Stück darüber – einmal nach
 * dem Fokus und nach dem Einblenden der Bildschirmtastatur, nie während einer Geste.
 */
function feldUeberDerHandlung(dialog: HTMLElement, feld: Element): void {
  const reihe = dialog.querySelector<HTMLElement>(DIALOG_HANDLUNG);
  if (!reihe || feld === reihe || reihe.contains(feld) || getComputedStyle(reihe).position !== "sticky") return;
  const ueber = feld.getBoundingClientRect().bottom - reihe.getBoundingClientRect().top + (parseFloat(getComputedStyle(dialog).rowGap) || 0);
  if (ueber > 0.5) dialog.scrollBy({ top: Math.ceil(ueber) });
}

function dialogHandlung(wurzel: HTMLElement): void {
  if (!wurzel.matches(".blattdialog")) return;
  if (!dialogeMitFokushilfe.has(wurzel)) {
    dialogeMitFokushilfe.add(wurzel);
    wurzel.addEventListener("focusin", (e) => {
      const feld = e.target instanceof Element ? e.target : null;
      if (!feld?.matches("input, textarea, select, [contenteditable]")) return;
      requestAnimationFrame(() => requestAnimationFrame(() => feldUeberDerHandlung(wurzel, feld)));
      window.setTimeout(() => { if (document.activeElement === feld) feldUeberDerHandlung(wurzel, feld); }, 400);
    });
  }
  const reihe = wurzel.querySelector<HTMLElement>(DIALOG_HANDLUNG);
  const abstand = reihe && getComputedStyle(reihe).position === "sticky"
    ? `${Math.ceil(reihe.getBoundingClientRect().height + (parseFloat(getComputedStyle(wurzel).rowGap) || 0))}px` : "";
  if (wurzel.style.scrollPaddingBottom !== abstand) wurzel.style.scrollPaddingBottom = abstand;
}

export function gestalten(wurzel: HTMLElement): void {
  ausrichtung(wurzel);
  woerter(wurzel);
  scrollGrenzen(wurzel);
  dialogHandlung(wurzel);
}

function beobachten(wurzel: HTMLElement): () => void {
  let bild = 0;
  let unterwegs = false;
  let erneut = false;
  const planen = () => {
    // kommt während eines Durchgangs etwas hinzu (z. B. die Schrift ist geladen), folgt danach ein weiterer
    if (unterwegs) { erneut = true; return; }
    unterwegs = true;
    erneut = false;
    bild = requestAnimationFrame(() => {
      gestalten(wurzel);
      // nach dem Messen ändert die Höhe der Scrollbereiche das Layout noch einmal: zweiter Durchgang im nächsten Bild
      bild = requestAnimationFrame(() => {
        scrollGrenzen(wurzel);
        // niedrige Höhe erst, wenn die Liste ihre Höhe hat; jede neue Stufe wird im nächsten Durchgang nachgemessen (K101)
        if (niedrigeHoehe(wurzel)) erneut = true;
        unterwegs = false;
        if (erneut) planen();
      });
    });
  };
  gestalten(wurzel);
  const groesse = new ResizeObserver(planen);
  groesse.observe(wurzel);
  // Der Hinweisstreifen ändert seinen Text beim Blättern; das allein gestaltet nicht neu (sonst schriebe jedes Blättern die
  // Höhe und Stelle der Liste neu und bräche auf iOS den Schwung der Wischgeste ab, N11).
  const inhalt = new MutationObserver((eintraege) => {
    const relevant = eintraege.filter((e) => !(e.target instanceof Element ? e.target : e.target.parentElement)?.closest(".mehr"));
    if (!relevant.length) return;
    inhaltGeaendert(relevant);
    planen();
  });
  inhalt.observe(wurzel, { childList: true, subtree: true, characterData: true });
  void document.fonts?.ready.then(planen);
  return () => { cancelAnimationFrame(bild); groesse.disconnect(); inhalt.disconnect(); };
}

/**
 * Gestaltung einer Ansicht: Callback-Ref für ihr Wurzelelement `.ansicht`. Misst nach jedem Bild, in dem sich Größe oder
 * Inhalt geändert haben, höchstens einmal je Bild; ein neues Wurzelelement (z. B. nach einem Dialog an Stelle der
 * Ansicht) wird neu beobachtet.
 */
export function useGestaltung(): (wurzel: HTMLElement | null) => void {
  const ende = useRef<(() => void) | null>(null);
  useEffect(() => () => { ende.current?.(); ende.current = null; }, []);
  return useCallback((wurzel: HTMLElement | null) => {
    ende.current?.();
    ende.current = wurzel ? beobachten(wurzel) : null;
  }, []);
}
